// /forgot: ask for a link to choose a new password, by email. /reset/<token>: the link;
// choose the new password, and you're signed in.

import { useEffect, useState, type FormEvent } from "react";
import {
  forgotPassword,
  mustSetUp,
  resetInfo,
  resetPassword,
  type ResetInfo,
} from "../api";
import { Link, navigate, useLocation } from "../router";
import { refreshMe, useSession } from "../session";
import { store } from "../store";
import { PageSplash } from "./Shell";
import { HxPage, HxWindow, Icon, NightMark } from "./hx";
import { PasswordInput } from "../ui/PasswordInput";

export function ForgotPage() {
  const { search } = useLocation();
  const [email, setEmail] = useState(search.get("email") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim()) return setError("Enter your email.");
    setBusy(true);
    setError(null);
    const r = await forgotPassword(email.trim());
    setBusy(false);
    if (r.ok) setSent(email.trim());
    else if (r.status === 429)
      setError("Too many requests. Try again in a few minutes.");
    else setError(r.error);
  };

  return (
    <HxPage testid="forgot">
      <div className="hx-column">
        {sent ? (
          <div
            className="hx-column-head hx-rise"
            role="status"
            data-testid="forgot-sent"
          >
            <span className="hx-check hx-check-lg">{Icon.check}</span>
            <h1 className="hx-h1">
              Check your <em>email.</em>
            </h1>
            <p>
              If there’s an account for <strong>{sent}</strong>, a link to
              choose a new password is on its way. It works once, for an hour.
              Nothing there in a few minutes? Check spam, or{" "}
              <button
                className="hx-linkbtn"
                onClick={() => setSent(null)}
                data-testid="forgot-again"
              >
                try again
              </button>
              .
            </p>
            <p className="hx-foot">
              <Link to="/login">Back to sign in</Link>
            </p>
          </div>
        ) : (
          <>
            <div className="hx-column-head hx-rise">
              <NightMark />
              <h1 className="hx-h1">
                Forgot your <em>password?</em>
              </h1>
              <p>We’ll email you a link to choose a new one.</p>
            </div>
            <div className="hx-rise" style={{ ["--i" as string]: 1 }}>
<HxWindow>
<form className="hx-form" onSubmit={submit} noValidate>
              <div className="hx-field">
                <label htmlFor="forgot-email">Email</label>
                <input
                  id="forgot-email"
                  placeholder="you@company.com"
                  name="email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                />
              </div>
              {error && (
                <div
                  className="hx-error"
                  role="alert"
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}
              <button
                className="hx-btn hx-btn-primary hx-btn-block"
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
              >
                {busy ? "Sending…" : "Email me a link"}
              </button>
            </form>
</HxWindow>
</div>
            <p className="hx-foot hx-rise" style={{ ["--i" as string]: 2 }}>
              Remembered it? <Link to="/login">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </HxPage>
  );
}

export function ResetPage({ token }: { token: string }) {
  const { auth } = useSession();
  const [info, setInfo] = useState<ResetInfo | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void resetInfo(token).then((r) =>
      setInfo(
        r.ok
          ? r.data
          : {
              valid: false,
              error:
                (r.body?.error as string) || "This link isn’t valid any more.",
            },
      ),
    );
  }, [token]);

  if (!info || auth.kind === "unknown") return <PageSplash />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 10)
      return setError("Use at least 10 characters for your password.");
    if (password !== confirm) return setError("The passwords don’t match.");
    setBusy(true);
    setError(null);
    const r = await resetPassword(token, password);
    if (!r.ok) {
      setBusy(false);
      if (r.status === 410) setInfo({ valid: false, error: r.error });
      else
        setError(
          r.status === 429
            ? "Too many attempts. Try again in a few minutes."
            : r.error,
        );
      return;
    }
    await refreshMe();
    setDone(true);
  };

  const next = () =>
    navigate(mustSetUp(store.get().me) ? "/start" : "/dashboard", {
      replace: true,
    });

  return (
    <HxPage testid="reset">
      <div className="hx-column">
        {done ? (
          <div
            className="hx-column-head hx-rise"
            role="status"
            data-testid="reset-done"
          >
            <span className="hx-check hx-check-lg">{Icon.check}</span>
            <h1 className="hx-h1">
              New password <em>set.</em>
            </h1>
            <p>
              You’re signed in here, and signed out of other browsers. Signing
              in the Mac app or cmx in another tab? Go back there and sign in
              with the new password.
            </p>
            <button
              className="hx-btn hx-btn-primary"
              onClick={next}
              data-testid="reset-continue"
            >
              Continue
            </button>
          </div>
        ) : info.valid ? (
          <>
            <div className="hx-column-head hx-rise">
              <NightMark />
              <h1 className="hx-h1">
                Choose a new <em>password.</em>
              </h1>
              <p>
                For <strong>{info.email}</strong>.
              </p>
            </div>
            <div className="hx-rise" style={{ ["--i" as string]: 1 }}>
<HxWindow>
<form className="hx-form" onSubmit={submit} noValidate>
              {/* Password managers save the new password under this account. */}
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={info.email ?? ""}
                readOnly
                hidden
              />
              <div className="hx-field">
                <label htmlFor="reset-password">New password</label>
                <PasswordInput
                  id="reset-password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password"
                />
                <span className="hx-field-hint">At least 10 characters.</span>
              </div>
              <div className="hx-field">
                <label htmlFor="reset-confirm">Confirm new password</label>
                <PasswordInput
                  id="reset-confirm"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm"
                />
              </div>
              {error && (
                <div
                  className="hx-error"
                  role="alert"
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}
              <button
                className="hx-btn hx-btn-primary hx-btn-block"
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
              >
                {busy ? "Saving…" : "Set new password"}
              </button>
            </form>
</HxWindow>
</div>
          </>
        ) : (
          <div
            className="hx-column-head hx-rise"
            role="alert"
            data-testid="reset-invalid"
          >
            <span className="hx-check hx-check-lg is-bad">!</span>
            <h1 className="hx-h1">
              This link <em>no longer works.</em>
            </h1>
            <p>
              Links to choose a new password work once, for an hour. Ask for a
              new one.
            </p>
            <Link className="hx-btn hx-btn-primary" to="/forgot">
              Email me a new link
            </Link>
          </div>
        )}
      </div>
    </HxPage>
  );
}
